import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Search, Bookmark, LogIn, LogOut, ShieldCheck, Menu, X, Sun, Moon, Mail, Crown } from "lucide-react";
import { CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import SocialIcons from "@/components/SocialIcons";
import AdSlot from "@/components/AdSlot";

export default function Layout({ children, breakingHeadlines = [] }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [dark, setDark] = useState(false);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="App min-h-screen flex flex-col bg-background text-foreground">
      {/* Breaking ticker */}
      {breakingHeadlines.length > 0 && (
        <div className="bg-primary text-primary-foreground" data-testid="breaking-ticker">
          <div className="max-w-[1400px] mx-auto flex items-center gap-4 px-4 py-2">
            <span className="text-xs font-black uppercase tracking-[0.25em] bg-background text-primary px-2 py-1 rounded-sm shrink-0">
              ಬ್ರೇಕಿಂಗ್
            </span>
            <Marquee gradient={false} speed={40} pauseOnHover>
              {breakingHeadlines.map((h, i) => (
                <Link
                  key={h.id}
                  to={`/article/${h.id}`}
                  className="mx-8 text-sm font-semibold hover:underline"
                  data-testid={`ticker-item-${i}`}
                >
                  ● {h.title}
                </Link>
              ))}
            </Marquee>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
              <div className="w-11 h-11 bg-primary text-primary-foreground grid place-items-center rounded-sm">
                <span className="font-kannada-heading text-2xl leading-none">
                  {(settings?.site_name || "ನ")[0]}
                </span>
              </div>
              <div className="leading-tight">
                <div className="font-kannada-heading text-xl sm:text-2xl" data-testid="brand-name-kn">
                  {settings?.site_name || "ನಮಸ್ತೆ ಕರ್ನಾಟಕ"}
                </div>
                <div className="font-ui text-[10px] tracking-[0.3em] uppercase text-muted-foreground" data-testid="brand-name-en">
                  {settings?.site_name_english || "Namaste Karnataka"} · Kannada Daily
                </div>
              </div>
            </Link>

            {/* Editor masthead badge — clickable to /about */}
            {settings?.editor_name && (
              <Link
                to="/about"
                className="hidden lg:flex items-center gap-2 pl-4 ml-2 border-l border-border hover:opacity-80 transition-opacity"
                data-testid="editor-masthead"
              >
                {settings.editor_photo_url && (
                  <img
                    src={settings.editor_photo_url}
                    alt=""
                    className="w-9 h-9 object-cover rounded-sm border border-border"
                  />
                )}
                <div className="leading-tight text-left">
                  <div className="font-ui text-[9px] uppercase tracking-[0.25em] text-muted-foreground">
                    {settings.editor_designation}
                  </div>
                  <div className="font-kannada-heading text-sm">{settings.editor_name}</div>
                </div>
              </Link>
            )}

            <form onSubmit={submitSearch} className="hidden md:flex items-center relative w-72">
              <Search className="w-4 h-4 absolute left-3 text-muted-foreground" />
              <Input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ಸುದ್ದಿ ಹುಡುಕಿ..."
                className="pl-9 rounded-none border-foreground/20 focus-visible:ring-primary"
              />
            </form>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDark}
                data-testid="theme-toggle"
                aria-label="Toggle theme"
              >
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <SocialIcons variant="header" className="hidden md:flex" />
              {(!user || user === false || user.subscription_tier !== "premium") && (
                <Button asChild variant="secondary" size="sm" className="hidden sm:inline-flex" data-testid="subscribe-cta">
                  <Link to="/subscribe">
                    <Crown className="w-4 h-4 mr-1" /> Premium
                  </Link>
                </Button>
              )}
              {user && user !== false && user.subscription_tier === "premium" && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground text-[10px] font-black uppercase tracking-widest rounded-sm" data-testid="premium-badge">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
              {user && user !== false ? (
                <>
                  <Button variant="ghost" size="sm" asChild data-testid="bookmarks-link">
                    <Link to="/bookmarks" className="hidden sm:inline-flex">
                      <Bookmark className="w-4 h-4 mr-1" /> ಬುಕ್‌ಮಾರ್ಕ್
                    </Link>
                  </Button>
                  {user.role === "admin" && (
                    <Button variant="secondary" size="sm" asChild data-testid="admin-link">
                      <Link to="/admin">
                        <ShieldCheck className="w-4 h-4 mr-1" /> Admin
                      </Link>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0]}
                  </Button>
                </>
              ) : (
                <Button asChild size="sm" data-testid="login-cta">
                  <Link to="/login">
                    <LogIn className="w-4 h-4 mr-1" /> ಲಾಗಿನ್
                  </Link>
                </Button>
              )}

              {/* Mobile menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <div className="mt-8 flex flex-col gap-1">
                    <form onSubmit={submitSearch} className="flex items-center relative mb-4">
                      <Search className="w-4 h-4 absolute left-3 text-muted-foreground" />
                      <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="ಸುದ್ದಿ ಹುಡುಕಿ..."
                        className="pl-9 rounded-none"
                      />
                    </form>
                    {CATEGORIES.map((c) => (
                      <NavLink
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className={({ isActive }) =>
                          `px-3 py-2 rounded-sm font-kannada-heading text-lg ${
                            isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                          }`
                        }
                      >
                        {c.name}
                      </NavLink>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Category nav */}
          <nav className="hidden md:flex items-center gap-1 py-2 overflow-x-auto" data-testid="category-nav">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold rounded-full border transition-colors ${
                  isActive
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:border-foreground"
                }`
              }
              data-testid="nav-home"
            >
              ಮುಖಪುಟ
            </NavLink>
            {CATEGORIES.map((c) => (
              <NavLink
                key={c.slug}
                to={`/category/${c.slug}`}
                className={({ isActive }) =>
                  `px-4 py-2 font-kannada-heading text-base rounded-full border transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-foreground text-background border-foreground"
                      : "border-border hover:border-foreground"
                  }`
                }
                data-testid={`nav-${c.slug}`}
              >
                {c.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* Top header ad */}
      <AdSlot slot="header" className="max-w-[1400px] mx-auto px-4 lg:px-6 pt-2 max-h-32" />

      <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 lg:px-6 py-8">{children}</main>

      <footer className="border-t border-border bg-muted/40 mt-16">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-10 grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="font-kannada-heading text-2xl">{settings?.site_name || "ನಮಸ್ತೆ ಕರ್ನಾಟಕ"}</div>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm font-kannada-body">
              {settings?.site_tagline || "ಕರ್ನಾಟಕದ ಪ್ರತಿ ಮೂಲೆಯ ಸುದ್ದಿಗಳು — ಕನ್ನಡದಲ್ಲಿ, ನಿಮ್ಮ ಬೆರಳ ತುದಿಯಲ್ಲಿ."}
            </p>
            {settings?.editor_name && (
              <div className="mt-4 flex items-center gap-3">
                {settings.editor_photo_url && (
                  <img src={settings.editor_photo_url} alt="" className="w-10 h-10 object-cover rounded-sm border border-border" />
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {settings.editor_designation}
                  </div>
                  <div className="font-kannada-heading text-base">{settings.editor_name}</div>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
              ವಿಭಾಗಗಳು
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="text-sm px-3 py-1 border border-border rounded-full hover:bg-foreground hover:text-background transition-colors"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Company
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/about" className="hover:text-primary transition-colors" data-testid="footer-about">
                ನಮ್ಮ ಬಗ್ಗೆ · About
              </Link>
              <Link to="/team" className="hover:text-primary transition-colors" data-testid="footer-team">
                ನಮ್ಮ ತಂಡ · Team
              </Link>
              <Link to="/contact" className="hover:text-primary transition-colors" data-testid="footer-contact">
                <Mail className="w-3 h-3 inline mr-1" /> ಸಂಪರ್ಕ · Contact
              </Link>
              <Link to="/subscribe" className="hover:text-primary transition-colors" data-testid="footer-subscribe">
                <Crown className="w-3 h-3 inline mr-1" /> Premium
              </Link>
              {settings?.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="text-muted-foreground hover:text-primary transition-colors break-all">
                  {settings.contact_email}
                </a>
              )}
              {settings?.contact_phone && (
                <a href={`tel:${settings.contact_phone}`} className="text-muted-foreground hover:text-primary transition-colors">
                  {settings.contact_phone}
                </a>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-6">
              © {new Date().getFullYear()} {settings?.site_name_english || "Namaste Karnataka"}.
            </p>
            <SocialIcons variant="footer" className="mt-4" />
          </div>
        </div>
      </footer>
    </div>
  );
}
